import { type Property, PropertyVisibility } from '~/models/property';

/**
 * Composable for managing property visibility filtering
 *
 * Visibility Rules:
 * - Public (0):      Everyone can see
 * - Beta (1):        Beta testers and admins only
 * - Hidden (2):      Admins only (private draft)
 * - PendingBeta (3): Owner and admins only (awaiting approval)
 *
 * In every case the property's owner can always see their own content.
 */
export const usePropertyVisibility = () => {
	const userStore = useUserStore();

	/**
	 * Check if the current user can view a specific property based on its visibility level
	 */
	const canViewProperty = (property: Property): boolean => {
		// If property doesn't have a visibility field, default to public (backward compatibility)
		const visibility = property.visibility ?? PropertyVisibility.Public;

		// Public properties are visible to everyone
		if (visibility === PropertyVisibility.Public) {
			return true;
		}

		// Admins can see everything
		if (userStore.isAdmin) {
			return true;
		}

		// Owners can always see their own non-public content (draft, pending, beta)
		if (property.ownerId && property.ownerId === userStore.id) {
			return true;
		}

		// Beta properties are visible to beta testers and admins
		if (visibility === PropertyVisibility.Beta) {
			return userStore.isBetaTester || userStore.isAdmin;
		}

		// Hidden and PendingBeta are only visible to admins (and owner, handled above)
		if (
			visibility === PropertyVisibility.Hidden ||
			visibility === PropertyVisibility.PendingBeta
		) {
			return userStore.isAdmin;
		}

		// Default: hide unknown visibility levels
		return false;
	};

	/**
	 * Filter an array of properties based on the current user's permissions
	 */
	const filterPropertiesByVisibility = (properties: Property[]): Property[] => {
		return properties.filter(canViewProperty);
	};

	/**
	 * Get visibility label for display
	 */
	const getVisibilityLabel = (visibility: PropertyVisibility): string => {
		switch (visibility) {
			case PropertyVisibility.Public:
				return 'Public';
			case PropertyVisibility.Beta:
				return 'Beta';
			case PropertyVisibility.PendingBeta:
				return 'Pending Beta';
			case PropertyVisibility.Hidden:
				return 'Hidden';
			default:
				return 'Unknown';
		}
	};

	/**
	 * Get visibility color for UI display
	 */
	const getVisibilityColor = (visibility: PropertyVisibility): string => {
		switch (visibility) {
			case PropertyVisibility.Public:
				return 'green';
			case PropertyVisibility.Beta:
				return 'orange';
			case PropertyVisibility.PendingBeta:
				return 'amber';
			case PropertyVisibility.Hidden:
				return 'red';
			default:
				return 'grey';
		}
	};

	return {
		canViewProperty,
		filterPropertiesByVisibility,
		getVisibilityLabel,
		getVisibilityColor,
	};
};
